// Player-screen copy that is not kudos: labels, states, errors. Rounds' voice: sentence case, plain, dry, UK English,
// no emoji, no exclamation marks. Kudos (the lines that make people smile) live in lib/kudos.ts.
import type { PublicItem } from '@shared/types';

type Named = Pick<PublicItem, 'format' | 'graphic'> & Partial<Pick<PublicItem, 'number' | 'hotspot' | 'multi'>>;

export function formatName(q: Named): string {
  switch (q.format) {
    case 'tf': return 'True or false';
    case 'spot': return 'Tap the culprit';
    case 'clues': return 'Diagnose in five';
    case 'gmcq': {
      const g = q.graphic;
      if (g?.type === 'bloods') return 'Read the bloods';
      if (g?.type === 'obs') return 'Read the obs';
      if (g?.type === 'ecg') return g.view === '12lead' ? 'Read the 12-lead' : 'Read the rhythm strip';
      return 'Read the results';
    }
    case 'number': return 'Work it out';
    case 'hotspot': return q.hotspot?.target === 'obs-param' ? 'Tap the observation' : q.hotspot?.target === 'ecg-lead' ? 'Tap the lead' : 'Tap the answer';
    case 'order': return 'Put them in order';
    case 'multi': return q.multi?.mode === 'rarity' ? 'Pick a rare one' : 'Pick all that apply';
    default: return 'Best single answer';
  }
}

/** "QT interval" stays as it is; "Anion gap" becomes "anion gap" in the middle of a sentence. */
export function midSentence(label: string): string {
  const t = label.trim().replace(/\.$/, '');
  const first = t.split(/\s+/)[0] ?? '';
  const acronym = /[A-Z].*[A-Z0-9]/.test(first) || /\d/.test(first);
  return acronym || !t ? t : t.charAt(0).toLowerCase() + t.slice(1);
}

export function formatHint(q: Named, screen = false): string {
  if (screen) return 'The question is on the big screen. Tap your answer here.';
  switch (q.format) {
    case 'tf': return 'One statement. Is it true?';
    case 'spot': return 'Tap the result that explains the case.';
    case 'clues': return 'A clue every few seconds. Answer early for more points.';
    case 'gmcq': return 'Study the results, then pick one answer.';
    case 'number': return q.number?.label ? `Work out the ${midSentence(q.number.label)}, then type it in.` : 'Work it out, then type the number in.';
    case 'hotspot': return q.hotspot?.target === 'obs-param' ? 'Tap the reading on the monitor that answers it.' : 'Tap the lead on the ECG that answers it.';
    case 'order': return 'Tap the steps in order, then submit. Tap again to undo.';
    case 'multi': return q.multi?.mode === 'rarity' ? 'Any right answer scores. The fewer people pick it, the more it is worth.' : 'Tap every right answer, then submit.';
    default: return 'Four options. One is right.';
  }
}

export const COPY = {
  join: {
    hero: 'Live battles for medics. Your phone is your buzzer.',
    code: 'Battle code', name: 'Name on the leaderboard', email: 'Email, for your result',
    sameEmail: "Joined before with this email? Your place and score come back with you.",
    submit: 'Join the battle', busy: 'Joining…', joined: "You're in",
    notYou: 'Not you?', edit: 'Change details', checking: 'Finding your place…',
    privacy: 'We use your name for the leaderboard and your email to send your result. Your details are remembered on this device so you can rejoin in one tap. MedSnapp Ltd is the data controller.',
    privacyLink: 'Privacy notice', termsLink: 'Prize terms',
    badCode: 'That code should be five letters or numbers.', needName: 'Add the name you want on the leaderboard.', badEmail: 'That email address does not look right.',
    network: 'Could not join. Check your connection and try again.',
    resume: {
      expired: 'Your last session on this phone has expired. Join again with the same email and your place and score come back with you.',
      missing: 'We could not find your place in this battle. Join again with the same email and your place and score come back with you.',
      offline: 'We could not reach the battle to put you back in. Check your connection, then join again with the same email: your place and score come back with you.',
    },
  },
  status: {
    lobby: 'Waiting to start', question: 'Live now', reveal: 'Live now', podium: 'Finished', ended: 'Finished',
    finished: 'This battle has finished.', live: 'Live now. Join and you are straight in.',
  },
  welcomeBack: (name: string) => `Welcome back, ${name}.`,
  rejoined: (name: string) => `Welcome back, ${name}. Your score came with you.`,
  lobby: {
    youreIn: (name: string) => (name ? `You're in, ${name}.` : "You're in."),
    waiting: 'The host will start soon.', startsIn: 'Starts in', at: 'at', startingNow: 'Starting any moment', held: 'The host has paused the start. Stay with us.',
    hostedBy: (name: string) => `Hosted by ${name}`,
    players: (n: number) => (n === 1 ? 'player in the room' : 'players in the room'),
    screen: 'Classroom mode: questions appear on the big screen, answers on your phone.',
    connecting: 'Connecting…', connected: 'Connected',
  },
  question: {
    // The lock-in rule for the footer lives with the formats (formats/names.ts, answerHint).
    of: (i: number, total: number) => `Question ${i + 1} of ${total}`,
    // The countdown already reads 0, so this says what comes next.
    expired: 'Results next.',
    // 9 Oct: the send states. Sending is a quiet cue; not sent opens the options again, and says what to do in three words.
    locked: (s: string) => `Locked in · ${s} s`, sending: 'Sending your answer…', notSent: 'Not sent. Tap again.',
    double: 'Double points', lightning: 'Lightning round', final: 'Final question',
  },
  reveal: {
    pending: 'Results are in', checking: 'Checking your answer…',
    lostHeadline: 'Not received', lostLine: 'Your answer did not reach us in time. Check your signal before the next one.',
    // The answers show the room's split, so the reveal adds a line only when there is no split to show.
    nobody: 'Nobody answered this one.',
    why: 'Why', answered: (n: number, of: number) => `${n.toLocaleString('en-GB')} of ${of.toLocaleString('en-GB')} answered`,
    boardIn: (n: number) => `Leaderboard in ${n}`,
  },
  board: {
    title: 'Leaderboard',
    next: (n: number) => `Next question in ${n}`, podium: (n: number) => `Podium in ${n}`, nextSoon: 'Next question coming up',
    held: 'The host has paused the battle. Back shortly.', manual: 'Next question when the host is ready.',
    you: 'You', of: (n: number) => `of ${n.toLocaleString('en-GB')}`, points: 'points', notRanked: 'Your rank appears after your first answer.',
  },
  podium: {
    title: 'The podium', you: 'Your result', rank: 'Rank', score: 'Points', accuracy: 'Accuracy', streak: 'Best streak', fastest: 'Fastest',
    see: 'See my result', notPlaced: 'You finished without a rank this time.',
  },
  result: {
    title: (name: string) => `${name}'s result`, loading: 'Loading your result…', none: 'No result to show. Join a battle first.',
    copy: 'Copy my result link', copied: 'Link copied',
  },
  /** Prizes (v2.1): what is on offer, how to win it, and where the terms are. */
  prize: {
    win: (phrase: string) => `Win ${phrase}.`,
    termsApply: 'Terms apply.',
    emailed: 'Winners are emailed within 7 days.',
    how: (winners: number, draws: number) => {
      const top = winners === 1 ? 'The top player' : `The top ${winners} players`;
      if (draws > 0) return `${winners === 1 ? 'The top player wins' : `The top ${winners} win`}, and ${draws} more ${draws === 1 ? 'is' : 'are'} drawn from players who enter the draw on their result page.`;
      return `${top} on the final leaderboard ${winners === 1 ? 'wins' : 'win'}.`;
    },
  },
  /** The claim form on the result page. */
  claim: {
    titleDraw: 'Claim your full result and enter the draw', title: 'Claim your full result',
    intro: 'Add your details to unlock every answer with its explanation.',
    firstName: 'First name', lastName: 'Last name', medSchool: 'Medical school', year: 'Year',
    draw: (phrase: string) => `Enter me in the prize draw for ${phrase}.`,
    agreeBefore: 'By entering the draw you agree to the ', agreeLink: 'prize terms', agreeAfter: '.',
    closed: (when?: string) => `${when ? `The prize draw closed ${when}.` : 'The prize draw has closed.'} You can still save your details to unlock your review.`,
    save: 'Save and unlock my review', saving: 'Saving…',
    saved: 'Saved.', savedDraw: "Saved. You're in the draw.", savedClosed: 'Saved. The prize draw had already closed, so this is not an entry.', savedBefore: 'Your details are saved.',
    reviewLater: 'Your question-by-question review appears once the battle has finished.',
    network: 'Could not save. Check your connection and try again.',
  },
  /** Round cards (v2.2): the rule card that opens each themed round. */
  round: {
    // "Round 2 of 6" and "Starting in 5" live with the RoundCard component (components/RoundCard.tsx).
    upNext: (i: number, total: number, name: string) => `Question ${i + 1} of ${total} · ${name}`,
  },
  /** The terms and privacy pages. */
  legal: {
    brand: 'MedSnapp Rounds',
    prizeTerms: 'Prize terms', battleTerms: 'Battle terms', privacy: 'Privacy notice',
    generalLead: 'These terms apply to every Rounds battle. A battle with prizes has its own prize terms, linked from its join page and lobby.',
    notFound: (code: string) => `We could not find a battle with the code ${code}, so these are the general battle terms.`,
    failed: 'We could not load these terms. Check your connection and try again.', retry: 'Try again',
    loading: 'Loading the terms…',
    updated: (date: string) => `Last updated ${date}`,
    back: 'Back to the battle', home: 'Back to Rounds', print: 'Print',
    contents: 'On this page', code: 'Battle code',
    drawCloses: (when: string) => `The draw closes ${when} (UK time).`, drawClosed: (when: string) => `The draw closed ${when} (UK time).`,
  },
} as const;
